import './styles.scoped.css'
import {useEffect, useState} from 'react'
import {Typography, Button, IconButton, Chip, Avatar, Menu, MenuItem} from '@mui/material'
import {ArrowBack, MoreVert, Edit} from '@mui/icons-material'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {useNavigate, Link, useParams} from 'react-router-dom'
import {getInitials, getColorFromName, parseError, thousantSeparator, ignoreTimezone} from '../../utils/helpers'
import {useMobile} from '../../hooks'
import {clientApi} from '../../services'
import {format} from 'date-fns'
import {SkeletonContainer, TextSkeleton, ChipSkeleton, AvatarSkeleton, ButtonSkeleton} from '../../components'

const Label = ({variant = 'h8', children}) => (
    <Typography variant={variant} color="text.primary">
        {children}
    </Typography>
)

const Divider = () => (
    <Typography variant="body2" color="text.primary" style={{marginLeft: 12, marginRight: 12}}>
        |
    </Typography>
)

const MainCardSkeleton = ({mobile}) => (
    <SkeletonContainer>
        <div className="main-card">
            <TextSkeleton width={mobile ? 220 : 290} fontSize={mobile ? 16 : 20} />
            <br />
            <div className="main-content">
                <div className="main-content-profile">
                    <AvatarSkeleton size={mobile ? 56 : 64} />
                    <div className="main-content-profile-name">
                        <TextSkeleton width={mobile ? 120 : 160} fontSize={mobile ? 16 : 20} />
                        <TextSkeleton width={mobile ? 96 : 120} fontSize={mobile ? 14 : 16} />
                    </div>
                </div>
                <div className="main-content-status">
                    <ChipSkeleton width={84} height={32} />
                    {!mobile && <TextSkeleton width={170} />}
                </div>
            </div>
            <br />
            {mobile ? (
                [
                    ['Payor:', 200],
                    ['MA/MCO Number:', 70],
                    ['Diagnosis Code:', 50],
                    ['Period:', 170],
                ].map(([label, width]) => (
                    <div className="content" key={label}>
                        <Label>{label}</Label>
                        <TextSkeleton width={width} />
                    </div>
                ))
            ) : (
                <>
                    <div className="content">
                        <Label>Payor:</Label>
                        <TextSkeleton width={200} />
                        <Divider />
                        <Label>MA/MCO Number:</Label>
                        <TextSkeleton width={70} />
                    </div>
                    <div className="content">
                        <Label>Diagnosis Code:</Label>
                        <TextSkeleton width={50} />
                    </div>
                </>
            )}
        </div>
    </SkeletonContainer>
)

const UnitsSkeleton = () => (
    <>
        <Label variant="h9">Used:</Label>
        <TextSkeleton width={72} />
        <Divider />
        <Label variant="h9">Remaining:</Label>
        <TextSkeleton width={72} />
    </>
)

const ServiceLinesSkeleton = ({mobile}) => (
    <SkeletonContainer>
        {[170, 140].map((width, index) => (
            <div key={index}>
                <br />
                <div className="line-card">
                    <div className="content-title">
                        <TextSkeleton width={width} fontSize={16} />
                        <ChipSkeleton width={index ? 64 : 56} />
                    </div>
                    <ChipSkeleton width={80} sx={{mt: 1.25}} />
                    <br />
                    <div className="content">
                        <Label variant="h9">Start Date:</Label>
                        <TextSkeleton width={76} />
                        <Divider />
                        <Label variant="h9">End Date:</Label>
                        <TextSkeleton width={76} />
                    </div>
                    {mobile ? (
                        <>
                            <div className="content">
                                <Label variant="h9">Total Units :</Label>
                                <TextSkeleton width={40} />
                            </div>
                            <div className="content">
                                <UnitsSkeleton />
                            </div>
                        </>
                    ) : (
                        <div className="content">
                            <Label variant="h9">Total Units :</Label>
                            <TextSkeleton width={40} />
                            <Divider />
                            <UnitsSkeleton />
                        </div>
                    )}
                    <br />
                    <ButtonSkeleton width={112} />
                </div>
            </div>
        ))}
    </SkeletonContainer>
)

export default () => {
    const [serviceAgreement, setServiceAgreement] = useState(null)
    const {setLoading, setMessageBox} = useGlobalStates()
    const navigate = useNavigate()
    const {mobile} = useMobile()
    const {clientId, serviceAgreementId} = useParams()
    const [anchorEl, setAnchorEl] = useState(null)
    const [skeleton, setSkeleton] = useState(true)

    useEffect(() => {
        setSkeleton(true)
        clientApi
            .getServiceAgreement(clientId, serviceAgreementId)
            .then(({data: {data}}) => {
                setSkeleton(false)
                setServiceAgreement(data)
            })
            .catch(error => {
                setSkeleton(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }, [])

    const menuOpen = e => {
        setAnchorEl(e.currentTarget)
    }

    const menuClose = () => {
        setAnchorEl(null)
    }

    const printPdf = () => {
        menuClose()
    }

    const removeServiceAgreement = () => {
        menuClose()
        setLoading(true)
        clientApi
            .deleteServiceAgreement(clientId, serviceAgreementId)
            .then(() => {
                setLoading(false)
                setMessageBox({
                    status: 'success',
                    title: 'Service Agreement Removed!',
                    description: 'The service agreement has been successfully deleted.',
                })
                navigate(`/clients-management/${clientId}/service-agreement`)
            })
            .catch(error => {
                setLoading(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }

    return (
        <>
            {mobile ? (
                <>
                    <div className="header">
                        <Link to={`/clients-management/${clientId}/service-agreement`}>
                            <IconButton>
                                <ArrowBack />
                            </IconButton>
                        </Link>
                        <Typography variant="h6" color="text.primary">
                            View Service Agreement
                        </Typography>
                    </div>
                    <br />
                    <br />
                    <br />
                    <div className="toolbar">
                        <Button
                            variant="outlined"
                            startIcon={<Edit />}
                            onClick={() =>
                                navigate(`/clients-management-nosublayout/${clientId}/service-agreement/service-agreement-form/${serviceAgreementId}`)
                            }>
                            Update
                        </Button>
                        <IconButton onClick={menuOpen}>
                            <MoreVert />
                        </IconButton>
                    </div>
                </>
            ) : (
                <>
                    <Typography variant="body1" color="text.secondary">
                        <Link className="link" to="/clients-management">
                            Clients Management
                        </Link>
                        {' / '}
                        <Link className="link" to={`/clients-management/${clientId}/service-agreement`}>
                            Service Agreement
                        </Link>{' '}
                        / View Service Agreement
                    </Typography>
                    <br />
                    <div className="header">
                        <div className="back-container">
                            <Link className="back-button" to={`/clients-management/${clientId}/service-agreement`}>
                                <ArrowBack style={{color: '#ffffff'}} fontSize="small" />
                            </Link>
                            <Typography variant="h5" color="text.primary">
                                View Service Agreement
                            </Typography>
                        </div>
                        <div className="actions">
                            <Button
                                variant="outlined"
                                startIcon={<Edit />}
                                onClick={() =>
                                    navigate(
                                        `/clients-management-nosublayout/${clientId}/service-agreement/service-agreement-form/${serviceAgreementId}`,
                                    )
                                }>
                                Update
                            </Button>
                            <IconButton onClick={menuOpen}>
                                <MoreVert />
                            </IconButton>
                        </div>
                    </div>
                </>
            )}
            <br />
            <br />
            {skeleton ? (
                <MainCardSkeleton mobile={mobile} />
            ) : mobile ? (
                <>
                    <div className="main-card">
                        <Typography variant="h8" color="text.primary">
                            Service Agreement No.: {serviceAgreement?.agreement_number}
                        </Typography>
                        <br />
                        <br />
                        <div className="main-content">
                            <div className="main-content-profile">
                                {serviceAgreement?.avatar_url ? (
                                    <Avatar src={serviceAgreement?.avatar_url} style={{width: 56, height: 56}} />
                                ) : (
                                    <Avatar
                                        style={{
                                            backgroundColor: getColorFromName(serviceAgreement?.client_name),
                                            width: 56,
                                            height: 56,
                                            fontSize: 28,
                                        }}>
                                        {getInitials(serviceAgreement?.client_name)}
                                    </Avatar>
                                )}
                                <div className="main-content-profile-name">
                                    <Typography variant="h8" color="text.primary">
                                        {serviceAgreement?.client_name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        PMI: {serviceAgreement?.client_ma_number}
                                    </Typography>
                                </div>
                            </div>
                            <div className="main-content-status">
                                {serviceAgreement?.status == 'active' && (
                                    <Chip
                                        label={`Active ${serviceAgreement?.service_items?.filter(item => item.status === 'active').length}/${serviceAgreement?.service_items?.length}`}
                                        color="success"
                                    />
                                )}
                                {serviceAgreement?.status == 'inactive' && <Chip label="Not Active" color="error" />}
                            </div>
                        </div>
                        <br />
                        <div className="content">
                            <Typography variant="h8" color="text.primary">
                                Payor:
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                                {serviceAgreement?.payer_name}
                            </Typography>
                        </div>
                        <div className="content">
                            <Typography variant="h8" color="text.primary">
                                MA/MCO Number:
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                                {serviceAgreement?.ma_mco_number}
                            </Typography>
                        </div>
                        <div className="content">
                            <Typography variant="h8" color="text.primary">
                                Diagnosis Code:
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                                {serviceAgreement?.diagnosis_code}
                            </Typography>
                        </div>
                        <div className="content">
                            <Typography variant="h8" color="text.primary">
                                Period:
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                                {format(new Date(ignoreTimezone(serviceAgreement?.effective_date) ?? null), 'MM/dd/yyyy') +
                                    ' - ' +
                                    format(new Date(ignoreTimezone(serviceAgreement?.through_date) ?? null), 'MM/dd/yyyy')}
                            </Typography>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div className="main-card">
                        <Typography variant="h6" color="text.primary">
                            Service Agreement No.: {serviceAgreement?.agreement_number}
                        </Typography>
                        <br />
                        <div className="main-content">
                            <div className="main-content-profile">
                                {serviceAgreement?.avatar_url ? (
                                    <Avatar src={serviceAgreement?.avatar_url} style={{width: 64, height: 64}} />
                                ) : (
                                    <Avatar
                                        style={{
                                            backgroundColor: getColorFromName(serviceAgreement?.client_name),
                                            width: 64,
                                            height: 64,
                                            fontSize: 32,
                                        }}>
                                        {getInitials(serviceAgreement?.client_name)}
                                    </Avatar>
                                )}
                                <div className="main-content-profile-name">
                                    <Typography variant="h6" color="text.primary">
                                        {serviceAgreement?.client_name}
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary">
                                        PMI: {serviceAgreement?.client_ma_number}
                                    </Typography>
                                </div>
                            </div>
                            <div className="main-content-status">
                                {serviceAgreement?.status == 'active' && (
                                    <Chip
                                        label={`Active ${serviceAgreement?.service_items?.filter(item => item.status === 'active').length}/${serviceAgreement?.service_items?.length}`}
                                        color="success"
                                    />
                                )}
                                {serviceAgreement?.status == 'inactive' && <Chip label="Not Active" color="error" />}
                                <Typography variant="body2" color="text.primary">
                                    {format(new Date(ignoreTimezone(serviceAgreement?.effective_date) ?? null), 'MM/dd/yyyy') +
                                        ' - ' +
                                        format(new Date(ignoreTimezone(serviceAgreement?.through_date) ?? null), 'MM/dd/yyyy')}
                                </Typography>
                            </div>
                        </div>
                        <br />
                        <div className="content">
                            <Typography variant="h8" color="text.primary">
                                Payor:
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                                {serviceAgreement?.payer_name}
                            </Typography>
                            <Typography variant="body2" color="text.primary" style={{marginLeft: 12, marginRight: 12}}>
                                |
                            </Typography>
                            <Typography variant="h8" color="text.primary">
                                MA/MCO Number:
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                                {serviceAgreement?.ma_mco_number}
                            </Typography>
                        </div>
                        <div className="content">
                            <Typography variant="h8" color="text.primary">
                                Diagnosis Code:
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                                {serviceAgreement?.diagnosis_code}
                            </Typography>
                        </div>
                    </div>
                </>
            )}
            <br />
            <br />
            <Typography variant="h7" color="primary.dark">
                Service Line
            </Typography>
            <br />
            {skeleton && <ServiceLinesSkeleton mobile={mobile} />}
            {serviceAgreement?.service_items?.map(serviceItem => (
                <div key={serviceItem?.id}>
                    <br />
                    <div className="line-card">
                        <div className="content-title">
                            <Typography variant="h8" color="secondary">
                                {serviceItem?.service_name} - {serviceItem?.service_code}
                            </Typography>
                            {serviceItem?.status == 'active' ? (
                                <Chip label="Active" size="small" color="success" />
                            ) : (
                                <Chip label="Inactive" size="small" color="error" />
                            )}
                        </div>
                        <Chip
                            label={serviceItem?.billing_unit?.replace('_', ' ')?.toUpperCase()}
                            size="small"
                            style={{fontWeight: 600, marginTop: 10, fontWeight: 'bold', backgroundColor: '#0000000A'}}
                        />
                        <br />
                        <br />
                        <div className="content">
                            <Typography variant="h9" color="text.primary">
                                Start Date:
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                                {format(new Date(ignoreTimezone(serviceItem?.start_date)), 'MM/dd/yyyy')}
                            </Typography>
                            <Typography variant="body2" color="text.primary" style={{marginLeft: 12, marginRight: 12}}>
                                |
                            </Typography>
                            <Typography variant="h9" color="text.primary">
                                End Date:
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                                {format(new Date(ignoreTimezone(serviceItem?.end_date)), 'MM/dd/yyyy')}
                            </Typography>
                        </div>
                        {mobile ? (
                            <>
                                <div className="content">
                                    <Typography variant="h9" color="text.primary">
                                        Total Units :
                                    </Typography>
                                    <Typography variant="body2" color="text.primary">
                                        {thousantSeparator(serviceItem?.total_units)}
                                    </Typography>
                                </div>
                                <div className="content">
                                    <Typography variant="h9" color="text.primary">
                                        Used:
                                    </Typography>
                                    <Typography variant="body2" color="text.primary">
                                        {'$' +
                                            thousantSeparator(serviceItem?.used_units) +
                                            ' (' +
                                            (serviceItem?.total_units == 0
                                                ? 0
                                                : Math.round((100 * serviceItem?.used_units) / serviceItem?.total_units)) +
                                            '%)'}
                                    </Typography>
                                    <Typography variant="body2" color="text.primary" style={{marginLeft: 12, marginRight: 12}}>
                                        |
                                    </Typography>
                                    <Typography variant="h9" color="text.primary">
                                        Remaining:
                                    </Typography>
                                    <Typography variant="body2" color="text.primary">
                                        {'$' +
                                            thousantSeparator(serviceItem?.remaining_units) +
                                            ' (' +
                                            (serviceItem?.total_units == 0
                                                ? 0
                                                : Math.round((100 * serviceItem?.remaining_units) / serviceItem?.total_units)) +
                                            '%)'}
                                    </Typography>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="content">
                                    <Typography variant="h9" color="text.primary">
                                        Total Units :
                                    </Typography>
                                    <Typography variant="body2" color="text.primary">
                                        {thousantSeparator(serviceItem?.total_units)}
                                    </Typography>
                                    <Typography variant="body2" color="text.primary" style={{marginLeft: 12, marginRight: 12}}>
                                        |
                                    </Typography>
                                    <Typography variant="h9" color="text.primary">
                                        Used:
                                    </Typography>
                                    <Typography variant="body2" color="text.primary">
                                        {'$' +
                                            thousantSeparator(serviceItem?.used_units) +
                                            ' (' +
                                            (serviceItem?.total_units == 0
                                                ? 0
                                                : Math.round((100 * serviceItem?.used_units) / serviceItem?.total_units)) +
                                            '%)'}
                                    </Typography>
                                    <Typography variant="body2" color="text.primary" style={{marginLeft: 12, marginRight: 12}}>
                                        |
                                    </Typography>
                                    <Typography variant="h9" color="text.primary">
                                        Remaining:
                                    </Typography>
                                    <Typography variant="body2" color="text.primary">
                                        {'$' +
                                            thousantSeparator(serviceItem?.remaining_units) +
                                            ' (' +
                                            (serviceItem?.total_units == 0
                                                ? 0
                                                : Math.round((100 * serviceItem?.remaining_units) / serviceItem?.total_units)) +
                                            '%)'}
                                    </Typography>
                                </div>
                            </>
                        )}
                        <br />
                        <Button
                            variant="outlined"
                            onClick={() =>
                                navigate(
                                    `/clients-management-nosublayout/${clientId}/service-agreement/view-service-agreement/${serviceAgreementId}/view-service-agreement-details/${serviceItem?.id}`,
                                )
                            }>
                            View Details
                        </Button>
                    </div>
                </div>
            ))}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={menuClose}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}>
                <div>
                    <MenuItem onClick={printPdf}>Print PDF</MenuItem>
                    <MenuItem onClick={removeServiceAgreement} sx={theme => ({color: theme.palette.error.main})}>
                        Remove
                    </MenuItem>
                </div>
            </Menu>
            <br />
            <br />
            <br />
            <br />
            <br />
        </>
    )
}
